import React from 'react';
import { SkinProps } from '../../../../types/component-skin';
import { formatCurrency } from '../../../../utils/formatUtils';

/**
 * 멤버십 가입 기본 스킨
 */
const BasicMembershipSignupSkin: React.FC<SkinProps> = ({ 
  data, 
  actions, 
  options, 
  mode, 
  utils 
}) => {
  const { t } = utils;
  
  // 데이터 추출
  const { 
    isRegistered,
    isChecking,
    isUserLoggedIn,
    prices,
    selectedPriceId,
    selectedAmount,
    productId,
    theme
  } = data;
  
  // 액션 추출
  const {
    handleMembershipSignup,
    handlePriceSelect,
    checkMembershipStatus
  } = actions;
  
  // 옵션 추출
  const {
    title = t('멤버십 가입신청'),
    buttonText = t('가입 신청'),
    completedText = t('가입 완료'),
    description = t('멤버십에 가입하시면 다양한 혜택을 받으실 수 있습니다.'),
    buttonColor = '#007bff',
    titleColor = '#333333',
    descriptionColor = '#666666'
  } = options;
  
  // 스타일 정의
  const containerStyle: React.CSSProperties = {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '40px 20px',
    textAlign: 'center' as const
  };
  
  const titleStyle: React.CSSProperties = {
    fontSize: '32px',
    fontWeight: 'bold',
    color: titleColor,
    marginBottom: '20px'
  };
  
  const descriptionStyle: React.CSSProperties = {
    fontSize: '16px',
    color: descriptionColor,
    lineHeight: '1.6',
    marginBottom: '40px'
  };
  
  const priceContainerStyle: React.CSSProperties = {
    marginBottom: '30px'
  };
  
  const priceOptionStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '15px 20px',
    margin: '10px 0',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  };
  
  const selectedPriceOptionStyle: React.CSSProperties = {
    ...priceOptionStyle,
    borderColor: buttonColor,
    backgroundColor: `${buttonColor}10`
  };
  
  const buttonStyle: React.CSSProperties = {
    padding: '16px 40px',
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#fff',
    backgroundColor: buttonColor,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    opacity: isChecking ? 0.6 : 1
  };
  
  const completedStyle: React.CSSProperties = {
    padding: '20px',
    backgroundColor: '#d4edda',
    color: '#155724',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: 'bold'
  };
  
  const loadingStyle: React.CSSProperties = {
    padding: '40px',
    fontSize: '16px',
    color: '#666'
  };
  
  const loginPromptStyle: React.CSSProperties = {
    padding: '40px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    marginTop: '20px'
  };
  
  // 로딩 중일 때
  if (isChecking) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>
          {t('멤버십 상태를 확인하고 있습니다...')}
        </div>
      </div>
    );
  }
  
  // 이미 등록된 경우
  if (isRegistered) {
    return (
      <div style={containerStyle}>
        <h2 style={titleStyle}>{title}</h2>
        <div style={completedStyle}>
          ✓ {completedText}
        </div>
        <p style={{ marginTop: '20px', color: descriptionColor }}>
          {t('이미 멤버십에 가입되어 있습니다.')}
        </p>
      </div>
    );
  }
  
  // 로그인되지 않은 경우
  if (!isUserLoggedIn) {
    return (
      <div style={containerStyle}>
        <h2 style={titleStyle}>{title}</h2>
        <p style={descriptionStyle}>{description}</p>
        <div style={loginPromptStyle}>
          <p style={{ marginBottom: '20px', fontSize: '16px' }}>
            {t('멤버십 가입을 위해 로그인이 필요합니다.')}
          </p>
          <button
            style={buttonStyle}
            onClick={() => utils.navigate && utils.navigate('/login')}
          >
            {t('로그인하기')}
          </button>
        </div>
      </div>
    );
  }
  
  // 상품 ID가 없는 경우
  if (!productId || productId <= 0) {
    return (
      <div style={containerStyle}>
        <h2 style={titleStyle}>{title}</h2>
        <p style={{ color: '#dc3545', fontSize: '16px' }}>
          {t('유효하지 않은 상품입니다.')}
        </p>
      </div>
    );
  }
  
  // 정상적인 가입 폼
  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>{title}</h2>
      <p style={descriptionStyle}>{description}</p>
      
      {/* 가격 옵션 */}
      {prices && prices.length > 0 && (
        <div style={priceContainerStyle}>
          <h3 style={{ marginBottom: '20px', fontSize: '20px', color: titleColor }}>
            {t('가격 옵션을 선택하세요')}
          </h3>
          {prices.map((price: any) => {
            const amount = parseFloat(price.unit_amount_decimal || '0');
            const isSelected = selectedPriceId === price.id.toString();
            
            return (
              <div
                key={price.id}
                style={isSelected ? selectedPriceOptionStyle : priceOptionStyle}
                onClick={() => handlePriceSelect(price.id.toString(), amount)}
              >
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '18px' }}>
                    {formatCurrency(amount, price.currency)}
                  </div>
                  {price.recurring && (
                    <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                      {t(`매 ${price.recurring.interval_count} ${price.recurring.interval}`)}
                    </div>
                  )}
                </div>
                <div>
                  <input
                    type="radio"
                    checked={isSelected}
                    onChange={() => {}}
                    style={{ width: '20px', height: '20px' }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* 가입 버튼 */}
      <button
        style={buttonStyle}
        onClick={handleMembershipSignup}
        disabled={isChecking || !selectedPriceId}
        onMouseEnter={(e) => {
          if (!isChecking && selectedPriceId) {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.15)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {buttonText}
      </button>
      
      {/* 무료 상품인 경우 메시지 */}
      {selectedAmount === 0 && (
        <p style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
          {t('무료 멤버십입니다. 결제 과정 없이 바로 가입됩니다.')}
        </p>
      )}
    </div>
  );
};

export default BasicMembershipSignupSkin;